import React from 'react';
import PropTypes from 'prop-types';
import { Button, FormControl, InputGroup } from 'react-bootstrap';
import ActionButton from 'components/common/ActionButton';

/**
 * Componente para un ítem individual del carrito
 */
const ProductoCarritoItem = ({
    product,
    index,
    onEliminar,
    onActualizar,
    onToggleEditar
}) => {
    const precioUnitarioIVA = product.dCantidad > 0
        ? (product.dTotal / product.dCantidad)
        : product.dPrecioUnitario * 1.16;

    const handleDecrementar = () => {
        if (product.dCantidad > 1) {
            onActualizar(product.index, product.dCantidad - 1, precioUnitarioIVA.toFixed(2));
        }
    };

    const handleIncrementar = () => {
        onActualizar(product.index, product.dCantidad + 1, precioUnitarioIVA.toFixed(2));
    };

    const handleConfirmarEdicion = () => {
        const cantidadEl = document.getElementById(`dCantidad${product.index}`);
        const precioEl = document.getElementById(`dPrecioUnitarioIVA${product.index}`);
        const cantidad = cantidadEl ? cantidadEl.value : product.dCantidad;
        const precioIVA = precioEl ? precioEl.value : precioUnitarioIVA.toFixed(2);
        onActualizar(product.index, cantidad, precioIVA);
        onToggleEditar(product.index, false);
    };

    return (
        <tr key={index} className="border-bottom align-middle">
            {/* Producto */}
            <td className="ps-3 py-2">
                <div className="d-flex align-items-center gap-2">
                    <div className="flex-shrink-0">
                        {product.vCodigoBarras === 'LIC-SERV' && (
                            <img src="/img/servidor48.png" width="32" alt="Servidor" />
                        )}
                        {product.vCodigoBarras === 'LIC-CAJ' && (
                            <img src="/img/caja48.png" width="32" alt="Caja" />
                        )}
                        {product.vCodigoBarras === 'LIC-NUB' && (
                            <img src="/img/nube48.png" width="32" alt="Nube" />
                        )}
                        {!['LIC-SERV', 'LIC-CAJ', 'LIC-NUB'].includes(product.vCodigoBarras) && (
                            <div
                                className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: 32, height: 32 }}
                            >
                                <span className="text-primary fs--2 fw-bold">
                                    {product.vNombreProducto.charAt(0)}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="lh-sm">
                        <div className="fw-semibold text-900">{product.vNombreProducto}</div>
                        {/* Mobile: SKU + price × qty = subtotal inline */}
                        <div className="d-sm-none text-muted fs--2 mt-1">
                            <span className="me-2">{product.vCodigoBarras}</span>
                            <span>
                                {precioUnitarioIVA.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                {' × '}{product.dCantidad}
                                {' = '}
                                <strong>{product.dTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</strong>
                            </span>
                        </div>
                    </div>
                </div>
            </td>

            {/* SKU — oculto en móvil */}
            <td className="py-2 text-muted fs--1 d-none d-sm-table-cell">{product.vCodigoBarras}</td>

            {/* P. Unitario IVA incl. — oculto en móvil y tablet pequeña */}
            <td className="py-2 text-end d-none d-md-table-cell">
                {product.bEdit ? (
                    <InputGroup size="sm" style={{ width: '120px', marginLeft: 'auto' }}>
                        <InputGroup.Text>$</InputGroup.Text>
                        <FormControl
                            type="number"
                            className="text-center"
                            id={`dPrecioUnitarioIVA${product.index}`}
                            defaultValue={precioUnitarioIVA.toFixed(2)}
                        />
                    </InputGroup>
                ) : (
                    <span className="fs--1">
                        {precioUnitarioIVA.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </span>
                )}
            </td>

            {/* Cantidad */}
            <td className="py-2 text-center">
                {product.bEdit ? (
                    <FormControl
                        type="number"
                        size="sm"
                        className="text-center mx-auto"
                        style={{ width: '70px' }}
                        id={`dCantidad${product.index}`}
                        defaultValue={product.dCantidad}
                    />
                ) : (
                    <div className="d-flex align-items-center justify-content-center gap-1">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="d-flex align-items-center justify-content-center p-0"
                            style={{ width: 24, height: 24 }}
                            onClick={handleDecrementar}
                        >
                            &minus;
                        </Button>
                        <span className="mx-2 fw-semibold" style={{ minWidth: '20px' }}>
                            {product.dCantidad}
                        </span>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="d-flex align-items-center justify-content-center p-0"
                            style={{ width: 24, height: 24 }}
                            onClick={handleIncrementar}
                        >
                            &#43;
                        </Button>
                    </div>
                )}
            </td>

            {/* Subtotal — oculto en móvil */}
            <td className="py-2 text-end fw-semibold fs--1 d-none d-sm-table-cell">
                {product.dTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </td>

            {/* Acciones */}
            <td className="py-2 pe-3 text-center">
                <div className="d-flex align-items-center justify-content-center gap-1">
                    {product.bEdit ? (
                        <ActionButton
                            onClick={handleConfirmarEdicion}
                            icon="check"
                            title="Confirmar"
                            variant="action"
                            className="p-0 text-success"
                        />
                    ) : (
                        <ActionButton
                            onClick={() => onToggleEditar(product.index, true)}
                            icon="edit"
                            title="Editar precio"
                            variant="action"
                            className="p-0"
                        />
                    )}
                    <ActionButton
                        onClick={onEliminar}
                        icon="trash-alt"
                        title="Eliminar"
                        variant="action"
                        className="p-0 text-danger"
                    />
                </div>
            </td>
        </tr>
    );
};

ProductoCarritoItem.propTypes = {
    product: PropTypes.shape({
        index: PropTypes.number.isRequired,
        idProducto: PropTypes.number.isRequired,
        vCodigoBarras: PropTypes.string.isRequired,
        vNombreProducto: PropTypes.string.isRequired,
        dPrecioUnitario: PropTypes.number.isRequired,
        dCantidad: PropTypes.number.isRequired,
        dImporte: PropTypes.number.isRequired,
        dDescuento: PropTypes.number,
        dIVA: PropTypes.number.isRequired,
        dTotal: PropTypes.number.isRequired,
        vClaveUnidad: PropTypes.string.isRequired,
        bEdit: PropTypes.bool,
        vTipoProducto: PropTypes.string,
        iTipoPago: PropTypes.number,
        vTipoRenovacion: PropTypes.string,
        idProductoRenovaciones: PropTypes.number,
        dTasaCuotaIVA: PropTypes.number,
        vTipoFactorIVA: PropTypes.string
    }).isRequired,
    index: PropTypes.number.isRequired,
    onEliminar: PropTypes.func.isRequired,
    onActualizar: PropTypes.func.isRequired,
    onToggleEditar: PropTypes.func.isRequired
};

export default ProductoCarritoItem;
